import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabaseServiceRole } from '../../services/supabase';
import { Database } from '../../types/supabase';
import { Button } from '../../components/ui/Button';

type CategoryInput = Database['public']['Tables']['categories']['Insert'];

const AdminCategoryForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const queryClient = useQueryClient();
    const isEditMode = Boolean(id);

    const [categoryForm, setCategoryForm] = useState<CategoryInput>({
        name: '',
        description: '',
        image_url: ''
    });
    const [error, setError] = useState<string | null>(null);

    // Получение данных категории для редактирования
    const { data: categoryData, isPending, error: queryError } = useQuery({
        queryKey: ['category', id],
        queryFn: async () => {
            try {
                if (!id) throw new Error('ID категории не указан');

                console.log('Запрос категории по ID (прямой доступ через компонент):', id);

                const { data, error } = await supabaseServiceRole
                    .from('categories')
                    .select('*')
                    .eq('id', id)
                    .single();

                if (error) {
                    console.error('Ошибка при получении категории:', error);
                    throw error;
                }

                return data;
            } catch (error) {
                console.error('Ошибка при получении категории:', error);
                throw error;
            }
        },
        enabled: !!id && isEditMode,
    });

    // Мутация для создания новой категории
    const createCategoryMutation = useMutation({
        mutationFn: async (newCategory: CategoryInput) => {
            try {
                console.log('Создание категории (прямой доступ через компонент):', newCategory);

                const { data, error } = await supabaseServiceRole
                    .from('categories')
                    .insert(newCategory)
                    .select();

                if (error) {
                    console.error('Ошибка создания категории:', error);
                    throw error;
                }

                return data;
            } catch (error) {
                console.error('Ошибка при создании категории:', error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
            navigate('/admin/categories');
        },
        onError: (error) => {
            console.error('Ошибка при создании категории:', error);
            setError('Не удалось создать категорию. Попробуйте еще раз.');
        }
    });

    // Мутация для обновления категории
    const updateCategoryMutation = useMutation({
        mutationFn: async (updatedCategory: CategoryInput) => {
            if (!id) throw new Error('ID категории не указан');

            try {
                console.log('Обновление категории (прямой доступ через компонент):', id, updatedCategory);

                const { data, error } = await supabaseServiceRole
                    .from('categories')
                    .update(updatedCategory)
                    .eq('id', id)
                    .select();

                if (error) {
                    console.error('Ошибка обновления категории:', error);
                    throw error;
                }

                return data;
            } catch (error) {
                console.error('Ошибка при обновлении категории:', error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
            navigate('/admin/categories');
        },
        onError: (error) => {
            console.error('Ошибка при обновлении категории:', error);
            setError('Не удалось обновить категорию. Попробуйте еще раз.');
        }
    });

    // Загрузка данных категории при получении данных для редактирования
    useEffect(() => {
        if (categoryData && isEditMode) {
            setCategoryForm({
                name: categoryData.name,
                description: categoryData.description || '',
                image_url: categoryData.image_url || ''
            });
        }
    }, [categoryData, isEditMode]);

    // Обработчик изменения полей формы
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setCategoryForm((prev) => ({ ...prev, [name]: value }));
    };

    // Обработчик отправки формы
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!categoryForm.name.trim()) {
            setError('Название категории обязательно');
            return;
        }

        const currentDate = new Date().toISOString();
        const formData = {
            ...categoryForm,
            updated_at: currentDate
        };

        if (isEditMode) {
            updateCategoryMutation.mutate(formData);
        } else {
            createCategoryMutation.mutate({
                ...formData,
                created_at: currentDate
            });
        }
    };

    if (isPending) {
        return (
            <div className="max-w-3xl mx-auto p-8">
                <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-apple-500">Загрузка данных...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container max-w-xl mx-auto pb-8">
            <h2 className="text-2xl font-bold mb-6 text-center text-apple-800">
                {isEditMode ? 'Редактирование категории' : 'Создание новой категории'}
            </h2>

            {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded border border-red-200">
                    {error}
                </div>
            )}

            {queryError && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded border border-red-200">
                    Ошибка загрузки категории. Пожалуйста, попробуйте снова.
                </div>
            )}

            {(isPending) ? (
                <div className="flex justify-center my-10">
                    <div className="spinner"></div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8">
                    <div className="mb-6">
                        <label htmlFor="name" className="block text-sm font-medium text-apple-700 mb-2">
                            Название категории*
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={categoryForm.name}
                            onChange={handleFormChange}
                            className="w-full px-4 py-2 border border-apple-200 rounded-apple focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="Например: Строительная техника"
                            required
                        />
                        <p className="mt-1 text-sm text-apple-500">
                            Укажите название категории, которое будет отображаться пользователям
                        </p>
                    </div>

                    <div className="mb-6">
                        <label htmlFor="description" className="block text-sm font-medium text-apple-700 mb-2">
                            Описание
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={categoryForm.description || ''}
                            onChange={handleFormChange}
                            className="w-full px-4 py-2 border border-apple-200 rounded-apple focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="Опишите категорию оборудования"
                            rows={4}
                        />
                        <p className="mt-1 text-sm text-apple-500">
                            Добавьте описание категории, которое поможет пользователям понять, какое оборудование в неё входит
                        </p>
                    </div>

                    <div className="mb-8">
                        <label htmlFor="image_url" className="block text-sm font-medium text-apple-700 mb-2">
                            URL изображения
                        </label>
                        <input
                            type="text"
                            id="image_url"
                            name="image_url"
                            value={categoryForm.image_url || ''}
                            onChange={handleFormChange}
                            className="w-full px-4 py-2 border border-apple-200 rounded-apple focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="https://example.com/image.jpg"
                        />
                        <p className="mt-1 text-sm text-apple-500">
                            Укажите ссылку на изображение, которое будет представлять категорию
                        </p>
                    </div>

                    {/* Предпросмотр изображения, если URL указан */}
                    {categoryForm.image_url && (
                        <div className="mb-8">
                            <label className="block text-sm font-medium text-apple-700 mb-2">
                                Предпросмотр изображения
                            </label>
                            <div className="h-40 w-40 rounded-apple overflow-hidden border border-apple-200">
                                <img
                                    src={categoryForm.image_url}
                                    alt="Предпросмотр"
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Error';
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end space-x-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => navigate('/admin/categories')}
                        >
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isEditMode ? updateCategoryMutation.isPending : createCategoryMutation.isPending}
                        >
                            {isEditMode ? (
                                updateCategoryMutation.isPending ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Сохранение...
                                    </>
                                ) : 'Сохранить изменения'
                            ) : (
                                createCategoryMutation.isPending ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Создание...
                                    </>
                                ) : 'Создать категорию'
                            )}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default AdminCategoryForm; 